import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { Spin } from 'antd';
import PageSortPanel from '@components/PageSortPanel';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useCampaignQuery } from '@hooks/queries/campaigns';
import { AssetItemDto, CampaignItemDto } from '@api/Api';
import { useLayout } from '@layouts/PageLayout';
import { useDebounced } from '@helpers/useDebounced';
import LottieComponent from '@components/Lottie';
import {
  useAssetReviewContentQuery,
  useAssetReviewQuery
} from '@hooks/queries/assetReview';

import {
  filterAssetsBySearchText,
  groupByFolder
} from '@pages/Campaigns/CampaignReview/utils';

import Head from './components/Head/Head';
import ListView from './components/ListView/ListView';
import TilesView from './components/TilesView/TilesView';

import './CampaignReview.less';

const SEARCH_PARAM = 'search';
const FOLDER_PARAM = 'folder';
const SORT_BY_PARAM = 'sortBy';

type CampaignReviewProps = {
  reviewId: string;
  campaign: CampaignItemDto;
};

function CampaignReview({ reviewId, campaign }: CampaignReviewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentView } = useLayout();
  const searchParam = (searchParams.get(SEARCH_PARAM) ?? '')
    .trim()
    .toLowerCase();
  const searchText = useDebounced(searchParam, 250);
  const sortByParam = searchParams.get(SORT_BY_PARAM) || 'createdAt:DESC';
  const folderParam = searchParams.get(FOLDER_PARAM) || '';

  const {
    data: assetReview,
    isLoading: isLoadingAssetReview,
    error: errorAssetReview
  } = useAssetReviewQuery({
    params: {
      reviewId: reviewId || ''
    },
    enabled: !!reviewId
  });

  const { data: reviewContent, isLoading: isLoadingReviewContent } =
    useAssetReviewContentQuery({
      params: {
        reviewId,
        password: assetReview?.review?.password
      },
      enabled: !!assetReview?.review?.id
    });

  const isLoading = isLoadingAssetReview || isLoadingReviewContent;

  const assetsGroupedByFolder = useMemo(() => {
    const assets = reviewContent?.assets || [];
    if (!assets.length) return { folders: [], withoutFolder: [] };
    return groupByFolder(assets);
  }, [reviewContent?.assets]);

  const selectedFolder = useMemo(() => {
    if (!folderParam) return null;
    return (
      assetsGroupedByFolder.folders.find(
        (folder) => folder.id === folderParam
      ) ?? null
    );
  }, [assetsGroupedByFolder.folders, folderParam]);

  const folders = useMemo(() => {
    let _folders = assetsGroupedByFolder.folders || [];
    if (searchText) {
      _folders = _folders
        .map((folder) => {
          return {
            ...folder,
            items: filterAssetsBySearchText(folder.items, searchText)
          };
        })
        .filter((folder) => !!folder.items.length);
    }
    return _folders;
  }, [assetsGroupedByFolder.folders, searchText]);

  const assetsList = useMemo(() => {
    let _assets = assetsGroupedByFolder.withoutFolder || [];
    if (selectedFolder) {
      _assets = selectedFolder.items || [];
    }
    if (searchText) {
      _assets = filterAssetsBySearchText(_assets, searchText);
    }
    if (sortByParam) {
      if (sortByParam === 'createdAt:DESC') {
        _assets = _assets.sort((a, b) => {
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        });
      }
      if (sortByParam === 'lastEventTime:DESC') {
        _assets = _assets.sort((a, b) => {
          return (
            new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
          );
        });
      }
    }
    return _assets;
  }, [
    assetsGroupedByFolder.withoutFolder,
    searchText,
    sortByParam,
    selectedFolder
  ]);

  const isEmpty = useMemo(() => {
    return !assetsList?.length && !folders?.length && !folderParam;
  }, [assetsList?.length, folders?.length, folderParam]);

  const onSelectFolder = useCallback(
    (folderId: string) => {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set(FOLDER_PARAM, folderId);
      setSearchParams(updatedParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onGoBack = useCallback(() => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete(FOLDER_PARAM);
    setSearchParams(updatedParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const onOpenAsset = useCallback(
    (asset: AssetItemDto) => {
      const versionId = asset.asset?.versions[0].id;
      if (asset?.deletedAt) return;
      if (!versionId) return;
      navigate(`/media-viewer/${asset.id}/${versionId}`, {
        state: { returnTo: location.pathname + location.search }
      });
    },
    [navigate, location.pathname, location.search]
  );

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 300px)'
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!assetReview) {
    return <Navigate to="/error-404" />;
  }

  if (errorAssetReview) {
    return <Navigate to={`/campaigns/single/${campaign.id}?tab=sharedLinks`} />;
  }

  return (
    <div className="campaign-review">
      <Head
        review={assetReview.review}
        selectedFolder={selectedFolder}
        campaignId={campaign.id}
        onGoBack={onGoBack}
      />
      <PageSortPanel
        isVisibleSortStyle={true}
        entity=" files"
        defaultSearchQuery={searchParam}
        defaultSortBy={sortByParam}
        onOrderBy={(key) => {
          searchParams.set(SORT_BY_PARAM, key);
          setSearchParams(searchParams);
        }}
        onSearch={(search) => {
          if (search) {
            searchParams.set(SEARCH_PARAM, search);
          } else {
            searchParams.delete(SEARCH_PARAM);
          }
          setSearchParams(searchParams, { replace: true });
        }}
      />
      <div className="campaign-review-content">
        {currentView === 'list' && (
          <ListView
            onSelectFolder={onSelectFolder}
            onOpenAsset={onOpenAsset}
            folders={folders}
            assetsList={assetsList}
            isOpenFolder={!!selectedFolder}
          />
        )}
        {currentView === 'tile' && (
          <TilesView
            onSelectFolder={onSelectFolder}
            onOpenAsset={onOpenAsset}
            folders={folders}
            assetsList={assetsList}
            isOpenFolder={!!selectedFolder}
          />
        )}
        {isEmpty && (
          <div className="campaign-review-empty">
            <LottieComponent
              className="campaign-review-empty-icon"
              view="addMediaInFolder"
            />
            <strong className="main-body-text main-body-text--bold">
              No assets found
            </strong>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CampaignReviewContainer() {
  const { reviewId: reviewIdParam, campaignId: campaignIdParam } = useParams();
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const campaignQuery = useCampaignQuery(
    { campaignId: campaignIdParam || '' },
    { enabled: !!campaignIdParam }
  );
  const campaign = campaignQuery?.data;

  useEffect(() => {
    if (campaign && campaign.workspaceId !== currentWorkspace?.id) {
      setCurrentWorkspace(campaign.workspaceId);
    }
  }, [campaign, currentWorkspace, setCurrentWorkspace]);
  if (!campaign || !currentWorkspace) return null;
  if (campaign.workspaceId !== currentWorkspace.id) return null;
  return <CampaignReview reviewId={reviewIdParam || ''} campaign={campaign} />;
}
